import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FaEye, FaEyeSlash, FaLock } from "react-icons/fa";
import { LoginAdmin, clearAuthError, getMeAdmin } from "../fitur/AuthSlice";
import { getDashboardPath } from "../config/roleConfig";
import useSWR from "swr";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import BackgroundImage from "../images/map4.jpg";
import Footer from "./Footer";

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === "https:" ? "https" : "http";
  const baseUrl = process.env.REACT_APP_URL.replace(/^https?:\/\//, "");
  return `${protocol}://${baseUrl}`;
};

const fetcher = (url) => axios.get(url).then((res) => res.data);

function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isError, isLoading, message } = useSelector(
    (state) => state.authAdmin
  );
  const { data, error } = useSWR(`${getApiBaseUrl()}/`, fetcher);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const savedUsername = localStorage.getItem("username");
    const savedPassword = localStorage.getItem("password");
    if (savedUsername && savedPassword) {
      setUsername(savedUsername);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    dispatch(clearAuthError());
    dispatch(getMeAdmin())
      .unwrap()
      .then((data) => {
        if (!cancelled && data?.role) {
          navigate(getDashboardPath(data.role), { replace: true });
        }
      })
      .catch(() => {
        if (!cancelled) {
          dispatch(clearAuthError());
        }
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, navigate]);

  useEffect(() => {
    if (user?.role && !isLoading && !isError) {
      navigate(getDashboardPath(user.role), { replace: true });
    }
  }, [user, isLoading, isError, navigate]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (rememberMe) {
      localStorage.setItem("username", username);
      localStorage.setItem("password", password);
    } else {
      localStorage.removeItem("username");
      localStorage.removeItem("password");
    }
    dispatch(LoginAdmin({ username, password }));
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          height: "auto",
          backgroundImage: `url(${BackgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
          padding: isMobile ? 2 : 4,
        }}
      >
        <Paper
          elevation={6}
          sx={{
            p: isMobile ? 3 : 4,
            width: "100%",
            maxWidth: 380,
            textAlign: "center",
            borderRadius: 3,
            bgcolor: "rgba(255, 255, 255, 0.95)",
            margin: "auto",
          }}
        >
          {/* Logo */}
          <img
            src="/images/logobaru.svg"
            alt="Logo Aplikasi"
            style={{ 
              width: isMobile ? "60px" : "80px", 
              marginBottom: isMobile ? "8px" : "10px" 
            }}
          />
          <Typography 
            variant={isMobile ? "subtitle1" : "h6"} 
            gutterBottom
            sx={{ fontSize: isMobile ? "0.9rem" : "1.25rem" }}
          >
            Sistem Informasi Digital Layanan dan Manajemen Perumahan
          </Typography>

          {/* Status Server */}
          {isError && (
            <Typography color="error" fontSize={isMobile ? "0.8rem" : "1rem"}>
              {message}
            </Typography>
          )}
          {error ? (
            <Typography color="error" fontSize={isMobile ? "0.8rem" : "1rem"}>
              Server Offline
            </Typography>
          ) : data ? (
            <Typography color="success" fontSize={isMobile ? "0.8rem" : "1rem"}>
              Server Online: {data}
            </Typography>
          ) : (
            <Typography fontSize={isMobile ? "0.8rem" : "1rem"}>
              Checking server status...
            </Typography>
          )}

          {/* Form */}
          <form onSubmit={handleLogin}>
            <TextField
              fullWidth
              label="Username"
              margin="normal"
              variant="filled"
              value={username}
              autoComplete="username"
              onChange={(e) => setUsername(e.target.value)}
              required
              size={isMobile ? "small" : "medium"}
              sx={{
                "& .MuiFilledInput-root": {
                  backgroundColor: "white",
                  borderRadius: "8px",
                },
                "& .MuiFilledInput-underline:before, & .MuiFilledInput-underline:after": {
                  display: "none",
                },
                "& .MuiInputLabel-root": {
                  fontSize: isMobile ? "0.8rem" : "1rem",
                },
                mt: isMobile ? 1 : 2,
              }}
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              margin="normal"
              variant="filled"
              value={password}
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
              required
              size={isMobile ? "small" : "medium"}
              sx={{
                "& .MuiFilledInput-root": {
                  backgroundColor: "white",
                  borderRadius: "8px",
                },
                "& .MuiFilledInput-underline:before, & .MuiFilledInput-underline:after": {
                  display: "none",
                },
                "& .MuiInputLabel-root": {
                  fontSize: isMobile ? "0.8rem" : "1rem",
                },
                mt: isMobile ? 1 : 2,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <FaEyeSlash size={isMobile ? 16 : 18} /> : <FaEye size={isMobile ? 16 : 18} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  size={isMobile ? "small" : "medium"}
                />
              }
              label={
                <Typography fontSize={isMobile ? "0.8rem" : "1rem"}>
                  Remember me
                </Typography>
              }
              sx={{ mt: 0.5 }}
            />

            {/* Tombol Login */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              size={isMobile ? "medium" : "large"}
              sx={{
                mt: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
                py: isMobile ? 1 : 1.5,
              }}
            >
              <FaLock size={isMobile ? 12 : 14} /> {isLoading ? "Loading..." : "Login"}
            </Button>

            {/* Tombol Reset Password */}
            <Button
              fullWidth
              variant="text"
              color="primary"
              onClick={() => navigate("/reset-password")}
              size={isMobile ? "small" : "medium"}
              sx={{ 
                mt: 1, 
                textTransform: "none",
                fontSize: isMobile ? "0.75rem" : "0.875rem"
              }}
            >
              Lupa Password? Reset di sini
            </Button>
             <Button
              fullWidth
              variant="text"
              color="primary"
              onClick={() => navigate("/register-masyarakat")}
              size={isMobile ? "small" : "medium"}
              sx={{ 
                mt: 1, 
                textTransform: "none",
                fontSize: isMobile ? "0.75rem" : "0.875rem"
              }}
            >
             Penerima Bantuan? Klik disini
            </Button>
          </form>
            {/* <Button
              fullWidth
              variant="text"
              color="primary"
              onClick={() => navigate("/registerMasyarakat")}
              size={isMobile ? "small" : "medium"}
              sx={{ 
                mt: 1, 
                textTransform: "none",
                fontSize: isMobile ? "0.75rem" : "0.875rem"
              }}
            >
              Register Akun Pengguna Baru
            </Button> */}
        </Paper>
      </Box>
      <Footer />
    </>
  );
}

export default Login;
